import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useDashboardData } from '../useDashboardData';
import { TileHint } from '../TileFrame';
import { DASH_COLORS, DASH_FONTS, DASH_TYPE } from '../dashboardTokens';
import AiAvatar from '../AiAvatar';

// AI cost ticker (#1160, specs/workshop-dashboard.md): running USD total
// exactly as logged (decision 2026-10-19 — no conversion to maintain), with
// a visible pulse per ai-call-logged so the tile moves even when cost does
// not. Expanded: per-feature breakdown, still aggregates only.

const pulseAnim = keyframes`
  0% { transform: scale(1); opacity: 1; }
  40% { transform: scale(2.2); opacity: 0.4; }
  100% { transform: scale(1); opacity: 1; }
`;

const HeadlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(10px, 1.4vmin, 32px);
  min-width: 0;
`;

// The full headline scale overflows a quarter-width tile once the total has
// digits + cents ("$12.66" is six characters); dollar amounts get their own,
// smaller scale.
const CostValue = styled.div`
  font-family: ${DASH_FONTS.numeric};
  font-size: clamp(30px, 5.5vmin, 130px);
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

const Pulse = styled.span`
  width: clamp(12px, 1.4vmin, 32px);
  height: clamp(12px, 1.4vmin, 32px);
  border-radius: 50%;
  background: ${DASH_COLORS.accent};
  animation: ${pulseAnim} 900ms ease;
`;

const FeatureTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${DASH_TYPE.meta};

  td {
    padding: clamp(6px, 0.8vmin, 16px) clamp(8px, 1vmin, 20px);
    border-bottom: 1px solid ${DASH_COLORS.tileBorder};
  }
  td.num {
    font-family: ${DASH_FONTS.numeric};
    text-align: right;
    white-space: nowrap;
  }
`;

// Cents while they matter; whole dollars once they don't.
function formatUsd(value) {
  return value >= 100 ? `$${Math.round(value)}` : `$${value.toFixed(2)}`;
}

export function CostTileBody({ t }) {
  const { data, aiPulse } = useDashboardData();
  const cost = data?.cost;
  if (!cost) return <TileHint>{t('dashboard.noData')}</TileHint>;
  return (
    <>
      <HeadlineRow>
        <AiAvatar />
        <CostValue>{formatUsd(cost.totalCostUsd)}</CostValue>
        {/* key remount restarts the animation on every logged AI call */}
        {aiPulse > 0 && <Pulse key={aiPulse} />}
      </HeadlineRow>
      <TileHint>
        {t('dashboard.costToday', { count: cost.callsToday, cost: cost.costTodayUsd.toFixed(2) })}
      </TileHint>
    </>
  );
}

export function CostTileDetail({ t }) {
  const { data } = useDashboardData();
  const cost = data?.cost;
  if (!cost) return <TileHint>{t('dashboard.noData')}</TileHint>;
  return (
    <>
      <TileHint>
        {t('dashboard.costToday', { count: cost.callsToday, cost: cost.costTodayUsd.toFixed(2) })}
      </TileHint>
      <FeatureTable>
        <tbody>
          {cost.byFeature.map((row) => (
            <tr key={row.feature}>
              <td>{row.feature}</td>
              <td className="num">{row.calls}</td>
              <td className="num">${row.cost_usd.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </FeatureTable>
    </>
  );
}
